import { getCartSectionIds } from '@theme/cart-add-helpers';
import { CartAddEvent } from '@theme/events';
import { formatMoney as formatShopMoney } from '@theme/money-formatting';

/**
 * The builder's two JSON blobs and its state. The variant shapes are written by
 * snippets/refillable-parfum-builder-block-b.liquid; every variant also carries the fields
 * of snippets/refillable-builder-availability.liquid.
 *
 * @typedef {object} BuilderConfig
 * @property {string} summaryZeroLabel
 * @property {number} maxPerScent
 * @property {{ starter?: OnetimeMinimum, refill?: OnetimeMinimum }} [onetimeMinimum]
 * @property {string} summaryUnitSingular
 * @property {string} summaryUnitPlural
 * @property {string} bannerTextOneRefill
 * @property {string} bannerTextTwoPlus
 * @property {string} soldOutLabel
 * @property {string} decreaseQuantityLabel
 * @property {string} removeScentLabel
 * @property {string} submitRequiresScent
 * @property {string} errorNoScent
 * @property {string} errorNoStarterVariant
 * @property {string} errorNoStepTwo
 * @property {string} errorAddFailed
 * @property {string} statusAdded
 * @property {string} planSubscriptionSentenceTemplate
 * @property {string} [planSubscriptionSentenceTemplateRefill]
 * @property {string} subscriptionScentClause
 * @property {string} topNotesLabel
 * @property {string} middleNotesLabel
 * @property {string} baseNotesLabel
 * @property {{ case?: string, refill?: string }} [heroOutlines]
 *
 * @typedef {{ minimum: number, hints: string[] }} OnetimeMinimum
 *
 * @typedef {object} BuilderVariant
 * @property {number} id
 * @property {string} title
 * @property {boolean} available
 * @property {'preorder' | 'sold_out' | ''} availabilityState
 * @property {string} preorderNoteHtml
 * @property {string} preorderBadgesHtml
 * @property {number} price
 * @property {string[]} options
 *
 * @typedef {BuilderVariant & { caseSlot: number, heroCaseLayer: string | null }} CaseVariant
 * @typedef {BuilderVariant & { caseSlot: number, scentSlot: number }} StarterPackVariant
 * @typedef {BuilderVariant & { scentSlot: number }} NoSprayVariant
 *
 * @typedef {object} RefillFields
 * @property {number} scentSlot
 * @property {string} displayTitle
 * @property {string} colourTitle
 * @property {string} scentCode
 * @property {string | null} image
 * @property {string | null} heroRefillLayer
 * @property {string | null} swatchImage
 * @property {string | null} scentColor
 * @property {string} badgesHtml
 * @property {string} descriptionHtml
 * @property {string} topNotesHtml
 * @property {string} middleNotesHtml
 * @property {string} baseNotesHtml
 *
 * @typedef {BuilderVariant & RefillFields} RefillVariant
 *
 * @typedef {object} SellingPlan
 * @property {number} id
 * @property {string} name
 * @property {number} intervalCount
 * @property {string} interval
 * @property {number} deliveryIntervalCount
 * @property {string} deliveryInterval
 *
 * @typedef {object} ProductFields
 * @property {number} id
 * @property {string} title
 * @property {number} basePrice
 * @property {string[]} [optionNames]
 * @property {number} [scentOptionIndex]
 * @property {number} [caseOptionIndex]
 *
 * @typedef {object} BuilderData
 * @property {number} shellProductId
 * @property {boolean} hasStarterPack
 * @property {number | null} defaultCaseVariantId
 * @property {number | null} defaultRefillVariantId
 * @property {(ProductFields & { variants: StarterPackVariant[] }) | null} starterPackProduct
 * @property {(ProductFields & { variants: CaseVariant[] }) | null} caseProduct
 * @property {ProductFields & { variants: RefillVariant[] }} refillProduct
 * @property {(ProductFields & { variants: NoSprayVariant[] }) | null} noSprayProduct
 * @property {SellingPlan[]} sellingPlans
 * @property {SellingPlan[]} starterPackSellingPlans
 *
 * @typedef {'starter' | 'refill'} BuilderMode
 *
 * @typedef {object} BuilderState
 * @property {BuilderMode} mode
 * @property {boolean} hasSelectedMode
 * @property {string | null} caseVariantId - A `c0`-style colour key with a starter pack, else a case variant ID.
 * @property {boolean} hasInteractedWithCaseVariant
 * @property {'subscription' | 'onetime'} plan
 * @property {string} frequency
 * @property {Record<string, number>} scentQuantities - Keyed by scent key, e.g. `s0`.
 * @property {string | null} subscriptionScentKey
 * @property {string | null} starterPackScentKey
 * @property {string | null} lastInteractedScentKey
 *
 * A scent as the cards and the cart see it, for the current mode and selection.
 * @typedef {object} ScentChoice
 * @property {string} key
 * @property {string} title
 * @property {string} colourTitle
 * @property {string} badgesHtml
 * @property {'regular' | 'preorder'} badgesVersion
 * @property {string} preorderNoteHtml
 * @property {string} scentColor
 * @property {number} variantId
 * @property {boolean} available
 * @property {boolean} refillAvailable
 * @property {boolean} refillOnly
 * @property {number} price
 * @property {string | null} image
 * @property {string} descriptionHtml
 * @property {string} topNotesHtml
 * @property {string} middleNotesHtml
 * @property {string} baseNotesHtml
 * @property {string | null} heroRefillLayer
 * @property {number | null} noSprayVariantId
 * @property {number | null} noSprayPrice
 *
 * @typedef {ScentChoice & { quantity: number }} SelectedScent
 * @typedef {{ key: string, available: boolean }} CaseColour
 * @typedef {{ id: number, quantity: number, selling_plan?: number }} CartLine
 */

      const elementName = 'refillable-parfum-builder-b';
      const __cfgEl = document.querySelector('script[data-refillable-builder-config]');
      /** @type {BuilderConfig} */
      const cfg = __cfgEl?.textContent ? JSON.parse(__cfgEl.textContent) : {};
      const refillableBuilderSummaryZeroLabel = cfg.summaryZeroLabel;
      const refillableBuilderSummaryUnitSingular = cfg.summaryUnitSingular;
      const refillableBuilderSummaryUnitPlural = cfg.summaryUnitPlural;
      const bannerTextOneRefill = cfg.bannerTextOneRefill;
      const bannerTextTwoPlus = cfg.bannerTextTwoPlus;
      const planSubscriptionSentenceTemplate = cfg.planSubscriptionSentenceTemplate || '';
      // Refill-only shoppers get their own plan copy: the refill selling plan prices the first
      // refill lower than the ones that follow, which the Starter Pack sentence does not say.
      const planSubscriptionSentenceTemplateRefill = cfg.planSubscriptionSentenceTemplateRefill || '';
      const subscriptionScentClause = cfg.subscriptionScentClause || '';

      // One opaque token rather than readable pairs. The builder no longer writes to the
      // address bar, and a legible query that never updates as you click reads as broken;
      // an encoded token makes no such promise. Inside, it is the same key/value payload.
      const URL_STATE_PARAM = 'b';
      const URL_STATE_KEYS = ['mode', 'case', 'scents', 'plan', 'freq'];
      // One ceiling for taps and shared links, so a shopper never runs into the stock limit.
      const MAX_PER_SCENT = Number(cfg.maxPerScent) > 0 ? Number(cfg.maxPerScent) : 10;

      // The renders below run on every tap and mostly re-assert values already on screen.
      // Writing an unchanged value still queues a mutation and can invalidate style, so
      // every update goes through these.
      /** @param {HTMLElement} element @param {boolean} hidden */
      const setHidden = (element, hidden) => {
        if (element.hidden !== hidden) element.hidden = hidden;
      };
      /** @param {Element} element @param {string} name @param {string} value */
      const setAttr = (element, name, value) => {
        if (element.getAttribute(name) !== value) element.setAttribute(name, value);
      };
      /** @param {Node} node @param {string} text */
      const setText = (node, text) => {
        if (node.textContent !== text) node.textContent = text;
      };
      /** @param {HTMLButtonElement} button @param {boolean} disabled */
      const setDisabled = (button, disabled) => {
        if (button.disabled !== disabled) button.disabled = disabled;
      };

      // Keep in step with snippets/refillable-builder-hero.liquid.
      const HERO_LAYER_IMAGES = {
        refill: { widths: [360, 480, 600, 720, 900, 1100], sizes: '(min-width: 750px) min(23vw, 460px), 49vw' },
        case: { widths: [120, 180, 240, 300], sizes: '(min-width: 750px) min(8.5vw, 170px), 18vw' }
      };

      // base64url, so the token survives a query string without percent-encoding.
      /** @param {string} payload */
      function encodeBuilderToken(payload) {
        try {
          return btoa(payload).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        } catch (error) {
          return '';
        }
      }

      /** @param {string} token */
      function decodeBuilderToken(token) {
        try {
          const normalised = String(token).replace(/-/g, '+').replace(/_/g, '/');
          const padded = normalised + '='.repeat((4 - (normalised.length % 4)) % 4);
          return atob(padded);
        } catch (error) {
          return null;
        }
      }

      /**
       * @template {Element} [T=HTMLElement]
       * @param {ParentNode | null | undefined} root
       * @param {string} selector
       * @returns {T | null}
       */
      function find(root, selector) {
        return /** @type {T | null} */ (root?.querySelector(selector) ?? null);
      }

      /**
       * @template {Element} [T=HTMLElement]
       * @param {ParentNode} root
       * @param {string} selector
       * @returns {T[]}
       */
      function findAll(root, selector) {
        return /** @type {T[]} */ (Array.from(root.querySelectorAll(selector)));
      }

      /**
       * @param {Element} element
       * @param {string} selector
       * @returns {HTMLElement | null}
       */
      function closest(element, selector) {
        return /** @type {HTMLElement | null} */ (element.closest(selector));
      }

      class RefillableParfumBuilder extends HTMLElement {
        /** @type {BuilderData} */ data;
        /** @type {HTMLButtonElement[]} */ modeButtons = [];
        /** @type {HTMLElement} */ caseStep;
        /** @type {HTMLElement} */ caseOptions;
        /** @type {HTMLElement} */ scentsStep;
        /** @type {HTMLElement} */ planStep;
        /** @type {HTMLElement[]} */ stepSections = [];
        /** @type {HTMLElement | null} */ planSentence = null;
        /** @type {HTMLElement | null} */ planScentSlot = null;
        /** @type {HTMLElement | null} */ planFrequencySlot = null;
        /** @type {HTMLButtonElement | null} */ planInfo = null;
        /** @type {HTMLSelectElement | null} */ subscriptionScentSelect = null;
        /** @type {HTMLSelectElement | null} */ frequencySelect = null;
        /** @type {HTMLElement | null} */ planPriceOneTime = null;
        /** @type {HTMLElement | null} */ planPriceSubscription = null;
        /** @type {HTMLElement | null} */ planPriceSubscriptionCompare = null;
        /** @type {HTMLElement | null} */ planPriceSubscriptionSale = null;
        /** @type {HTMLElement} */ scentGrid;
        /** @type {HTMLElement} */ summary;
        /** @type {HTMLElement | null} */ incentiveText = null;
        /** @type {string} */ incentiveDefaultText = '';
        /** @type {HTMLElement} */ total;
        /** @type {HTMLElement | null} */ ctaPriceNote = null;
        /** @type {HTMLElement} */ status;
        /** @type {HTMLElement | null} */ statusError = null;
        /** @type {HTMLButtonElement} */ submitButton;
        /** @type {boolean} */ scentsFirst = false;
        /** @type {HTMLElement | null} */ submitHint = null;
        /** @type {string} */ onetimeHint = '';
        /** @type {HTMLElement | null} */ ctaFixed = null;
        /** @type {HTMLElement | null} */ _ctaFixedNode = null;
        /** @type {HTMLElement | null} */ productInformationRoot = null;
        /** @type {HTMLElement | null} */ productInformationSection = null;
        /** @type {(HTMLElement & { slideshow?: { select(index: number, event?: Event, options?: { animate?: boolean }): void } }) | null} */
        mediaGallery = null;
        /** @type {HTMLElement | null} */ mediaGalleryCaption = null;
        /** @type {HTMLElement | null} */ mediaGalleryCaptionHeader = null;
        /** @type {HTMLElement | null} */ mediaGalleryCaptionTitle = null;
        /** @type {HTMLElement | null} */ mediaGalleryCaptionTitleText = null;
        /** @type {HTMLElement | null} */ mediaGalleryCaptionDescription = null;
        /** @type {HTMLElement | null} */ mediaGalleryCaptionNotes = null;
        /** @type {HTMLElement | null} */ primaryMediaHost = null;
        /** @type {HTMLElement | null} */ primaryMediaOverlay = null;
        /** @type {HTMLImageElement | null} */ primaryMediaRefillImage = null;
        /** @type {HTMLImageElement | null} */ primaryMediaCaseImage = null;
        /** @type {ResizeObserver | undefined} */ _ctaFixedResizeObserver;
        /** @type {((event: KeyboardEvent) => void) | undefined} */ _ctaTabBridge;
        /** @type {(() => void) | undefined} */ _closePlanInfo;
        /** @type {((event: KeyboardEvent) => void) | undefined} */ _closePlanInfoOnEscape;
        /** @type {ReturnType<typeof setTimeout> | undefined} */ _addedResetTimeout;
        /** @type {{ codeToSlot: Map<string, string>, slotToCode: Map<string, string> } | undefined} */ _scentUrlMaps;
        _captionMaxHeight = 0;
        /** @type {string | undefined} */ _captionKey;
        /** @type {string | undefined} */ _planSentenceKey;

        constructor() {
          super();
          /** @type {BuilderState} */
          this.state = {
            mode: 'starter',
            hasSelectedMode: false,
            caseVariantId: null,
            hasInteractedWithCaseVariant: false,
            plan: 'subscription',
            frequency: '3',
            scentQuantities: {},
            subscriptionScentKey: null,
            starterPackScentKey: null,
            lastInteractedScentKey: null
          };
          this.isSubmitting = false;
          this.moneyFormat = '{{amount}}';
          this.currency = 'EUR';
          this.hasStarterPack = false;
          this.refillScentIndex = -1;
          this.noSprayScentIndex = -1;
          this.usesAutomaticNoSpraySplit = false;
        }

        /**
         * @param {string} selector
         * @returns {HTMLElement}
         */
        required(selector) {
          const element = find(this, selector);
          if (!element) throw new Error(`Refillable builder is missing ${selector}`);
          return element;
        }

        connectedCallback() {
          const data = this.parseData();
          if (!data) return;
          this.data = data;
          this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
          this.currency = this.dataset.currency || 'EUR';
          this.hasStarterPack = this.dataset.hasStarterPack === 'true';
          this.refillScentIndex = this.data.refillProduct.scentOptionIndex ?? -1;
          this.noSprayScentIndex = this.data.noSprayProduct?.scentOptionIndex ?? -1;
          this.usesAutomaticNoSpraySplit = Boolean(this.data.noSprayProduct?.variants?.length);
          this.state.caseVariantId = this.getDefaultCaseKey();
          this.state.lastInteractedScentKey = this.getDefaultScentKey();

          // Scoped to step 1: the scent cards' quantity buttons carry a data-mode of their own.
          this.modeButtons = findAll(this, '[data-step="product"] [data-mode]');
          this.caseStep = this.required('[data-step="case"]');
          this.caseOptions = this.required('[data-step="case"] [data-step-options]');
          this.scentsStep = this.required('[data-step="scents"]');
          this.scentsFirst = Boolean(this.scentsStep.compareDocumentPosition(this.caseStep) & 4);
          this.planStep = this.required('[data-step="plan"]');
          this.stepSections = findAll(this, '[data-step]');
          this.planSentence = find(this, '[data-plan-sentence]');
          this.planSubscriptionTitle = find(this, '[data-plan-subscription-title]');
          this.planScentSlot = find(this, '[data-plan-slot="scent"]');
          this.planFrequencySlot = find(this, '[data-plan-slot="frequency"]');
          this.planInfo = find(this, '[data-plan-info]');
          this.subscriptionScentSelect = find(this, '[data-subscription-scent-select]');
          this.frequencySelect = find(this, '[data-frequency-select]');
          this.shortenFrequencyOptions();
          this.planPriceOneTime = find(this, '[data-plan-price="onetime"]');
          this.planPriceSubscription = find(this, '[data-plan-price="subscription"]');
          this.planPriceSubscriptionCompare = find(this, '[data-plan-price-compare="subscription"]');
          this.planPriceSubscriptionSale = find(this, '[data-plan-price-sale="subscription"]');
          this.scentGrid = this.required('[data-scent-grid]');
          this.summary = this.required('[data-summary]');
          this.incentiveText = find(this, '[data-incentive-text]');
          this.incentiveDefaultText = this.incentiveText?.textContent || '';
          this.total = this.required('[data-total]');
          this.ctaPriceNote = find(this, '[data-cta-price-note]');
          this.status = this.required('[data-status]');
          this.statusError = find(this, '[data-status-error]');
          this.submitButton = /** @type {HTMLButtonElement} */ (this.required('[data-submit]'));
          this.submitHint = find(this, '[data-submit-hint]');
          this.ctaFixed = find(this, '[data-cta-fixed]') || this._ctaFixedNode || null;
          this.productInformationRoot = this.closest('[data-testid="product-information"]')
            || this.closest('.product-information')
            || find(this.closest('.product-details')?.closest('.shopify-section'), '[data-testid="product-information"]')
            || null;
          this.productInformationSection = this.productInformationRoot?.closest('.shopify-section')
            || this.closest('.shopify-section')
            || this.closest('[id^="shopify-section-"]')
            || null;
          this.mediaGallery = find(this.productInformationRoot, 'media-gallery');
          this.mediaGalleryCaption = find(this.productInformationRoot, '[data-media-gallery-caption]');
          this.mediaGalleryCaptionHeader = find(this.mediaGalleryCaption, '[data-media-gallery-caption-header]');
          this.mediaGalleryCaptionTitle = find(this.mediaGalleryCaption, '[data-media-gallery-caption-title]');
          this.mediaGalleryCaptionTitleText = find(this.mediaGalleryCaption, '[data-media-gallery-caption-title-text]');
          this.mediaGalleryCaptionDescription = find(this.mediaGalleryCaption, '[data-media-gallery-caption-description]');
          this.mediaGalleryCaptionNotes = find(this.mediaGalleryCaption, '[data-media-gallery-caption-notes]');

          this.applyUrlState();

          this.renderModeOptions();
          this.renderPlanOptions();
          this.renderFrequencyOptions();
          this.renderStepTwoOptions();
          this.renderScents();
          this.orderScentCards();
          this.bindEvents();
          this.renderSummary();
          this.emitStateChange();
          this.initCtaFixedBar();
        }

        /** @returns {BuilderData | null} */
        parseData() {
          const script = find(this, '[data-builder-json]');
          if (!script?.textContent) return null;
          try {
            return JSON.parse(script.textContent);
          } catch (error) {
            console.error('Failed to parse refillable builder data', error);
            return null;
          }
        }

        bindEvents() {
          this.modeButtons.forEach((button) => {
            button.addEventListener('click', () => {
              this.state.hasSelectedMode = true;
              this.setMode(button.dataset.mode === 'refill' ? 'refill' : 'starter');
            });
          });

          findAll(this, '[data-plan]').forEach((button) => {
            button.addEventListener('click', () => this.setPlan(button.dataset.plan === 'onetime' ? 'onetime' : 'subscription'));
          });

          this.caseOptions.addEventListener('click', (event) => {
            const option = event.target instanceof Element
              ? /** @type {HTMLElement | null} */ (event.target.closest('[data-step-two-option]'))
              : null;
            if (option?.dataset.stepTwoOption) this.selectCaseOption(option.dataset.stepTwoOption);
          });

          this.scentGrid.addEventListener('click', (event) => this.handleScentGridClick(event));

          find(this, '[data-discovery-set-line]')?.addEventListener('click', () => {
            window.gtag?.('event', 'discovery_set_line_click');
            window.Shopify?.analytics?.publish('discovery_set_line_click', {});
          });

          const frequencySelect = this.frequencySelect;
          frequencySelect?.addEventListener('change', () => {
            this.setFrequency(frequencySelect.value);
          });

          const subscriptionScentSelect = this.subscriptionScentSelect;
          subscriptionScentSelect?.addEventListener('change', () => {
            this.state.subscriptionScentKey = subscriptionScentSelect.value;
            this.autoSelectStarterPackScent();
            this.renderPlanSentence();
            this.renderSummary();
            this.emitStateChange();
          });

          // A native select opens on Space or the arrow keys but ignores Enter, and these two read
          // as links inside a sentence, so Enter is the key people reach for. Where showPicker is
          // unsupported we leave the default alone rather than swallow the key.
          [subscriptionScentSelect, frequencySelect].forEach((select) => {
            if (!select) return;
            select.addEventListener('keydown', (event) => {
              if (event.key !== 'Enter' || typeof select.showPicker !== 'function') return;
              event.preventDefault();
              try {
                select.showPicker();
              } catch (error) {
                // Older engines throw when the picker cannot be opened programmatically.
              }
            });
          });

          const planInfo = this.planInfo;
          planInfo?.addEventListener('click', (event) => {
            event.stopPropagation();
            this.togglePlanInfo(planInfo.getAttribute('aria-expanded') !== 'true');
          });

          this._closePlanInfo = () => this.togglePlanInfo(false);
          this._closePlanInfoOnEscape = (event) => {
            if (event.key === 'Escape') this.togglePlanInfo(false);
          };
          document.addEventListener('click', this._closePlanInfo);
          document.addEventListener('keydown', this._closePlanInfoOnEscape);

          this.submitButton?.addEventListener('click', () => {
            this.submitSelection();
          });
        }

        disconnectedCallback() {
          this._ctaFixedResizeObserver?.disconnect();
          document.body.style.paddingBottom = '';
          document.body.style.removeProperty('--bottom-bar-height');
          this.ctaFixed?.remove();
          if (this._ctaTabBridge) document.removeEventListener('keydown', this._ctaTabBridge);
          if (this._closePlanInfo) document.removeEventListener('click', this._closePlanInfo);
          if (this._closePlanInfoOnEscape) document.removeEventListener('keydown', this._closePlanInfoOnEscape);
        }

        initCtaFixedBar() {
          const ctaFixed = this.ctaFixed;
          if (!ctaFixed) return;
          this._ctaFixedNode = ctaFixed;

          // .product-details is `position: sticky` on desktop, which makes it a stacking
          // context — a fixed bar rendered inside it is trapped and painted under later
          // sections (e.g. variant-swatch-columns). Relocate it to the product-information
          // wrapper, which is not a stacking context and carries the section colour scheme,
          // so it lands in the root stacking context. Falls back to <body>.
          const ctaFixedHost = this.productInformationRoot || document.body;
          if (ctaFixed.parentElement !== ctaFixedHost) {
            ctaFixedHost.appendChild(ctaFixed);
          }

          // The bar is permanently fixed to the viewport bottom, so it no longer
          // reserves space in normal flow — pad the page so it never covers content.
          // The observer reports the height after layout; reading offsetHeight here
          // forced a full-page layout in the middle of start-up.
          /** @param {number} height */
          const applyClearance = (height) => {
            const padding = `${Math.round(height)}px`;
            if (document.body.style.paddingBottom !== padding) {
              document.body.style.paddingBottom = padding;
              // Read by other fixed UI (the WhatsApp button) to sit above the bar.
              document.body.style.setProperty('--bottom-bar-height', padding);
            }
          };

          if ('ResizeObserver' in window) {
            this._ctaFixedResizeObserver = new ResizeObserver(([entry]) => {
              if (!entry) return;
              applyClearance(entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height);
            });
            this._ctaFixedResizeObserver.observe(ctaFixed);
          } else {
            const measure = () => applyClearance(ctaFixed.offsetHeight);
            requestAnimationFrame(measure);
            globalThis.addEventListener('resize', measure);
          }

          this.initCtaTabBridge();
        }

        // Relocating the bar (above) puts it last in the product-information wrapper, so Tab only
        // reaches add-to-cart after the accordions below the builder — even though the bar is
        // pinned to the viewport directly under it. Bridge the gap so focus order follows what is
        // actually on screen: last builder control -> bar -> whatever follows the builder.
        initCtaTabBridge() {
          const ctaFixed = this.ctaFixed;
          if (!ctaFixed) return;

          const FOCUSABLE =
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

          // offsetParent is null for position:fixed, so the bar's own buttons fail that test —
          // use client rects plus visibility, which also drops the visibility:hidden controls.
          /** @param {HTMLElement} el */
          const isVisible = (el) =>
            !(/** @type {HTMLButtonElement} */ (el).disabled) && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
          /** @param {ParentNode} root */
          const stopsIn = (root) => findAll(root, FOCUSABLE).filter(isVisible);

          this._ctaTabBridge = (event) => {
            if (event.key !== 'Tab') return;

            const active = document.activeElement;
            if (!active) return;

            const inBuilder = this.contains(active);
            const inBar = ctaFixed.contains(active);
            if (!inBuilder && !inBar) return;

            const barStops = stopsIn(ctaFixed);
            const firstBarStop = barStops[0];
            if (!firstBarStop) return;

            if (inBuilder && !event.shiftKey) {
              const builderStops = stopsIn(this);
              if (active !== builderStops[builderStops.length - 1]) return;
              event.preventDefault();
              firstBarStop.focus();
              return;
            }

            if (inBar && event.shiftKey && active === firstBarStop) {
              const lastBuilderStop = stopsIn(this).at(-1);
              if (!lastBuilderStop) return;
              event.preventDefault();
              lastBuilderStop.focus();
              return;
            }

            // Leaving the bar forwards should continue below the builder, not wherever the
            // relocated bar happens to sit.
            if (inBar && !event.shiftKey && active === barStops.at(-1)) {
              const next = findAll(document, FOCUSABLE).find(
                (el) =>
                  !this.contains(el) &&
                  !ctaFixed.contains(el) &&
                  this.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING &&
                  isVisible(el)
              );
              if (!next) return;
              event.preventDefault();
              next.focus();
            }
          };

          document.addEventListener('keydown', this._ctaTabBridge);
        }

        /**
         * @param {BuilderVariant | null | undefined} variant
         * @param {number} scentIndex
         */
        getScentValueFromVariant(variant, scentIndex) {
          if (!variant) return '';

          const options = variant.options || [];
          const optionValue = scentIndex > -1 ? options[scentIndex] : '';
          return optionValue || variant.title || '';
        }

        /**
         * @param {{ scentSlot?: number | null } | null | undefined} variant
         */
        getScentSlotKey(variant) {
          if (!variant || variant.scentSlot == null) return '';
          return `s${variant.scentSlot}`;
        }

        /**
         * @param {{ caseSlot?: number | null } | null | undefined} variant
         */
        getCaseSlotKey(variant) {
          if (!variant || variant.caseSlot == null) return '';
          return `c${variant.caseSlot}`;
        }

        /**
         * @template {BuilderVariant & { scentSlot: number }} V
         * @param {V[]} variants
         * @param {number} scentIndex
         * @returns {Map<string, V & { scentKey: string, scentValue: string }>}
         */
        buildVariantMapByScent(variants, scentIndex) {
          const variantMap = new Map();

          (variants || []).forEach((variant) => {
            const scentKey = this.getScentSlotKey(variant);
            if (!scentKey) return;

            const scentValue = this.getScentValueFromVariant(variant, scentIndex);

            const currentVariant = variantMap.get(scentKey);
            if (!currentVariant || (!currentVariant.available && variant.available)) {
              variantMap.set(scentKey, {
                ...variant,
                scentKey,
                scentValue
              });
            }
          });

          return variantMap;
        }

        // Refill-only builds take the refill discount when one is set; the Starter Pack discount
        // covers everything else, including a refill discount left at 0.
        getSubscriptionDiscountRate() {
          const starterPercent = Number(this.dataset.subscribeDiscount || 0);
          const refillPercent = Number(this.dataset.refillSubscribeDiscount || 0);
          const percent = this.state.mode === 'refill' && refillPercent > 0 ? refillPercent : starterPercent;
          const rate = percent / 100;
          return rate > 0 && rate < 1 ? rate : 0;
        }

        getPlanSubscriptionLabel() {
          const refillLabel = this.dataset.planSubscriptionLabelRefill || '';
          if (this.state.mode === 'refill' && refillLabel) return refillLabel;
          return this.dataset.planSubscriptionLabel || '';
        }

        getPlanSubscriptionSentenceTemplate() {
          if (this.state.mode === 'refill' && planSubscriptionSentenceTemplateRefill) {
            return planSubscriptionSentenceTemplateRefill;
          }
          return planSubscriptionSentenceTemplate;
        }

        renderPlanSubscriptionTitle() {
          if (!this.planSubscriptionTitle) return;
          const label = this.getPlanSubscriptionLabel();
          if (label) setText(this.planSubscriptionTitle, label);
        }

        /**
         * @param {unknown} value
         * @returns {string}
         */
        normalizeColorValue(value) {
          if (!value) return '';
          if (typeof value === 'string') return value.trim();
          if (typeof value === 'object') {
            const color = /** @type {Record<string, unknown>} */ (value);
            const candidate = color.hex || color.value || color.rgb || color.hsl || color.color || color.code || '';
            return typeof candidate === 'string' ? candidate.trim() : '';
          }

          return '';
        }

        /**
         * @param {unknown} color
         */
        setProductInformationBackground(color) {
          const normalizedColor = this.normalizeColorValue(color);
          // The builder always carries it for the add-to-cart colour; the section (the page
          // background) only when that setting is on.
          const pageTarget = this.dataset.pageBackgroundColor === 'true'
            ? this.productInformationSection || this.productInformationRoot
            : null;

          [this, pageTarget].forEach((target) => {
            if (!target) return;
            if (normalizedColor) {
              target.style.setProperty('--refillable-builder-product-info-background', normalizedColor);
            } else {
              target.style.removeProperty('--refillable-builder-product-info-background');
            }
          });
        }

        getCaseVariants() {
          return Array.isArray(this.data?.caseProduct?.variants)
            ? this.data.caseProduct.variants
            : [];
        }

        getDefaultCaseVariantId() {
          const explicitId = this.data?.defaultCaseVariantId;
          if (explicitId != null) {
            return String(explicitId);
          }

          const caseVariants = this.getCaseVariants();
          const fallbackVariant = caseVariants.find((variant) => variant.available) || caseVariants[0];
          return fallbackVariant ? String(fallbackVariant.id) : null;
        }

        getDefaultCaseKey() {
          return this.hasStarterPack ? this.getDefaultStarterPackCaseColourKey() : this.getDefaultCaseVariantId();
        }

        /**
         * @param {string | null} id
         */
        getCaseVariantById(id) {
          return (id && this.getCaseVariants().find((variant) => String(variant.id) === id)) || null;
        }

        getDefaultScentKey() {
          const scents = this.getScentChoices();
          if (!scents.length) return null;

          // The opening scent the page was rendered with (snippets/refillable-builder-defaults.liquid).
          const rendered = this.dataset.defaultScentKey;
          if (this.state.mode === 'starter' && rendered && scents.some((scent) => scent.key === rendered)) {
            return rendered;
          }

          const explicitId = this.data?.defaultRefillVariantId;
          if (explicitId != null) {
            const match = scents.find((scent) => String(scent.variantId) === String(explicitId));
            if (match) return match.key;
          }

          const fallback = scents.find((scent) => scent.available) || scents[0];
          return fallback ? fallback.key : null;
        }

        getEffectiveCaseVariant() {
          if (this.state.mode !== 'starter') return null;
          if (this.hasStarterPack) {
            return this.getCaseVariantForColourKey(this.state.caseVariantId);
          }
          return this.getCaseVariantById(this.state.caseVariantId) || this.getCaseVariantById(this.getDefaultCaseVariantId());
        }

        /**
         * @param {string | null} colourKey
         */
        getCaseVariantForColourKey(colourKey) {
          if (!colourKey) return null;
          const caseVariants = this.getCaseVariants();
          return caseVariants.find((v) => this.getCaseSlotKey(v) === colourKey) || null;
        }

        getStarterPackVariants() {
          return Array.isArray(this.data?.starterPackProduct?.variants)
            ? this.data.starterPackProduct.variants
            : [];
        }

        /**
         * @returns {CaseColour[]}
         */
        getStarterPackCaseColours() {
          const variants = this.getStarterPackVariants();
          const seen = new Map();
          variants.forEach((v) => {
            const key = this.getCaseSlotKey(v);
            if (!key || seen.has(key)) return;
            seen.set(key, {
              key: key,
              available: variants.some((vv) => this.getCaseSlotKey(vv) === key && vv.available)
            });
          });
          return Array.from(seen.values());
        }

        /**
         * @param {string | null} caseColourKey
         * @param {string | null} scentKey
         */
        getStarterPackVariantForSelection(caseColourKey, scentKey) {
          const variants = this.getStarterPackVariants();
          return variants.find((v) => {
            return this.getCaseSlotKey(v) === caseColourKey
              && this.getScentSlotKey(v) === scentKey;
          }) || null;
        }

        // A scent with no starter-pack variant cannot open a bundle — the bundle IS that
        // variant. It can still ride along as an extra refill, so it is offered only while
        // an eligible scent is selected, and cleared when the last one goes.
        /**
         * @param {string} scentKey
         */
        isStarterEligible(scentKey) {
          if (!this.hasStarterPack) return true;
          const caseKey = this.state.caseVariantId || this.getDefaultStarterPackCaseColourKey();
          return Boolean(this.getStarterPackVariantForSelection(caseKey, scentKey));
        }

        hasStarterEligibleSelection() {
          return Object.entries(this.state.scentQuantities)
            .some(([key, quantity]) => Number(quantity) > 0 && this.isStarterEligible(key));
        }

        /**
         * @param {RefillVariant} refill
         * @returns {Pick<ScentChoice, 'badgesHtml' | 'badgesVersion' | 'preorderNoteHtml'>}
         */
        getScentCardPresentation(refill) {
          const isPreorder = this.state.mode === 'refill' && refill.available && refill.availabilityState === 'preorder';
          const showsPreorderBadges = Boolean(isPreorder && refill.preorderBadgesHtml);
          return {
            badgesHtml: showsPreorderBadges ? refill.preorderBadgesHtml : refill.badgesHtml || '',
            badgesVersion: showsPreorderBadges ? 'preorder' : 'regular',
            preorderNoteHtml: isPreorder ? refill.preorderNoteHtml || '' : ''
          };
        }

        getDefaultStarterPackCaseColourKey() {
          const colours = this.getStarterPackCaseColours();
          const rendered = this.dataset.defaultCaseKey;
          if (rendered && colours.some((c) => c.key === rendered)) return rendered;
          const available = colours.find((c) => c.available);
          return available ? available.key : (colours[0]?.key || null);
        }

        getSelectedStarterPackVariant() {
          if (!this.hasStarterPack || this.state.mode !== 'starter') return null;
          const caseKey = this.state.caseVariantId;
          const scentKey = this.state.starterPackScentKey;
          if (!caseKey || !scentKey) return null;
          return this.getStarterPackVariantForSelection(caseKey, scentKey);
        }

        autoSelectStarterPackScent() {
          if (!this.hasStarterPack || this.state.mode !== 'starter') return;
          // Only a scent the starter pack actually stocks can be the primary; picking one it
          // does not would resolve to no variant and throw on add to cart.
          const selected = this.getSelectedScents().filter((s) => this.isStarterEligible(s.key));
          const [first] = selected;
          if (!first) {
            this.state.starterPackScentKey = null;
            return;
          }
          if (selected.length === 1) {
            this.state.starterPackScentKey = first.key;
            return;
          }
          if (this.state.subscriptionScentKey) {
            const subScent = selected.find((s) => s.key === this.state.subscriptionScentKey);
            if (subScent) {
              this.state.starterPackScentKey = subScent.key;
              return;
            }
          }
          this.state.starterPackScentKey = first.key;
        }

        ensurePrimaryMediaOverlay() {
          if (this.primaryMediaOverlay?.isConnected && this.primaryMediaHost?.isConnected) {
            return this.primaryMediaOverlay;
          }

          const primaryMediaHost = find(this.productInformationRoot, '.product-information__media .product-media-container .product-media');
          if (!(primaryMediaHost instanceof HTMLElement)) {
            return null;
          }

          // snippets/refillable-builder-hero.liquid renders the overlay, and its CSS, with the
          // layers the opening state shows; any it left out are created here. With no overlay
          // at all there is no CSS either, so the product photo is left as it is.
          const overlay = find(primaryMediaHost, '[data-refillable-media-overlay]');
          if (!overlay) return null;

          this.primaryMediaHost = primaryMediaHost;
          this.primaryMediaHost.classList.add('refillable-builder__media-host');
          this.primaryMediaOverlay = overlay;
          this.primaryMediaRefillImage = find(overlay, '[data-refillable-media-refill]') || this.createHeroLayer(overlay, 'refill');
          this.primaryMediaCaseImage = find(overlay, '[data-refillable-media-case]') || this.createHeroLayer(overlay, 'case');

          return overlay;
        }

        /**
         * @param {HTMLElement} overlay
         * @param {'refill' | 'case'} kind
         */
        createHeroLayer(overlay, kind) {
          const image = document.createElement('img');
          image.className = 'refillable-builder__media-layer';
          image.setAttribute(`data-refillable-media-${kind}`, '');
          image.alt = '';
          image.decoding = 'async';
          image.fetchPriority = 'high';
          image.hidden = true;
          // The refill sits before the case, which the paired layout's flex order relies on.
          if (kind === 'refill') overlay.prepend(image);
          else overlay.append(image);
          return image;
        }

        /**
         * @param {HTMLImageElement | null} image
         * @param {string | null | undefined} src
         * @param {boolean} isVisible
         * @param {{ widths: number[], sizes: string }} imageSizes
         * @param {string} [alt]
         */
        updateHeroLayer(image, src, isVisible, imageSizes, alt = '') {
          if (!(image instanceof HTMLImageElement)) return;

          const normalizedSrc = src ? String(src) : '';
          if (normalizedSrc && image.getAttribute('src') !== normalizedSrc) {
            image.setAttribute('src', normalizedSrc);
            image.srcset = this.getHeroLayerSrcset(normalizedSrc, imageSizes.widths);
            image.sizes = imageSizes.sizes;
          }

          setAttr(image, 'alt', alt);
          setHidden(image, !isVisible);
          image.classList.toggle('is-visible', isVisible);
        }

        /**
         * @param {string} src
         * @param {number[]} widths
         */
        getHeroLayerSrcset(src, widths) {
          let url;
          try {
            url = new URL(src, window.location.href);
          } catch (error) {
            return '';
          }
          if (!url.searchParams.has('width')) return '';

          return widths.map((width) => {
            url.searchParams.set('width', String(width));
            return `${url.href} ${width}w`;
          }).join(', ');
        }

        selectHeroMediaSlide() {
          const slideshow = this.mediaGallery?.slideshow;
          if (!slideshow || typeof slideshow.select !== 'function') return;

          slideshow.select(0, undefined, { animate: false });
        }

        /**
         * @param {ScentChoice | null} [visualScent]
         */
        syncRefillableHeroMedia(visualScent = null) {
          const overlay = this.ensurePrimaryMediaOverlay();
          if (!(overlay instanceof HTMLElement) || !(this.primaryMediaHost instanceof HTMLElement)) {
            return;
          }

          const effectiveScent = visualScent || this.getVisualScent();
          const effectiveCaseVariant = this.getEffectiveCaseVariant();
          const hasVisualScent = Boolean(effectiveScent);
          const outlines = cfg.heroOutlines || {};
          const hasCaseChoice = hasVisualScent || this.state.hasInteractedWithCaseVariant;
          // A slot the shopper hasn't filled shows a dotted outline instead of the product photo.
          const refillSource = effectiveScent
            ? (effectiveScent.heroRefillLayer || (this.state.mode === 'refill' ? (effectiveScent.image || '') : ''))
            : (outlines.refill || '');
          const caseSource = this.state.mode === 'starter'
            ? (hasCaseChoice ? (effectiveCaseVariant?.heroCaseLayer || '') : (outlines.case || ''))
            : '';
          const showRefill = Boolean(refillSource);
          const showCase = Boolean(caseSource);
          const isActive = showRefill || showCase;
          const prevActive = this.primaryMediaHost.dataset.refillableMediaActive === 'true';
          const layout = showRefill && showCase
            ? 'paired'
            : (showRefill ? 'refill-only' : (showCase ? 'case-only' : 'empty'));

          setHidden(overlay, !isActive);
          setAttr(overlay, 'data-refillable-media-layout', layout);
          setAttr(this.primaryMediaHost, 'data-refillable-media-active', isActive ? 'true' : 'false');
          this.updateHeroLayer(
            this.primaryMediaRefillImage,
            refillSource,
            showRefill,
            HERO_LAYER_IMAGES.refill,
            effectiveScent ? `${this.data?.refillProduct?.title || ''} – ${effectiveScent.colourTitle}` : ''
          );
          this.updateHeroLayer(this.primaryMediaCaseImage, caseSource, showCase, HERO_LAYER_IMAGES.case);

          if (isActive && !prevActive) {
            this.selectHeroMediaSlide();
          }
        }

        /**
         * @param {HTMLElement} qtyWrap
         * @param {number} quantity
         */
        updateScentQtyMinusButton(qtyWrap, quantity) {
          const minusBtn = find(qtyWrap, '[data-delta="-1"]');
          if (!minusBtn) return;

          const isRemoveState = quantity <= 1;
          const label = isRemoveState ? cfg.removeScentLabel : cfg.decreaseQuantityLabel;
          const minusIcon = find(minusBtn, '.refillable-builder__qty-icon--minus');
          const deleteIcon = find(minusBtn, '.refillable-builder__qty-icon--delete');

          setAttr(minusBtn, 'data-mode', isRemoveState ? 'remove' : 'decrease');
          setAttr(minusBtn, 'aria-label', label);

          const minusLabelEl = find(minusBtn, '.refillable-builder__qty-minus-label');
          if (minusLabelEl) setText(minusLabelEl, label);

          if (minusIcon) setHidden(minusIcon, isRemoveState);
          if (deleteIcon) setHidden(deleteIcon, !isRemoveState);
        }

        /**
         * @param {number | string} variantId
         */
        getRefillVariantById(variantId) {
          return this.data?.refillProduct?.variants?.find((variant) => String(variant.id) === String(variantId)) || null;
        }

        /**
         * @returns {ScentChoice | null}
         */
        getVisualScent() {
          const scents = this.getScentChoices();
          const lastInteractedScent = scents.find((scent) => {
            return String(scent.key) === String(this.state.lastInteractedScentKey);
          });

          if (lastInteractedScent) {
            return lastInteractedScent;
          }

          return this.getSelectedScents().at(-1) ?? null;
        }

        /**
         * @param {string} label
         * @param {string | undefined} html
         */
        renderMediaGalleryCaptionNote(label, html) {
          const noteHtml = String(html || '').trim();
          if (!noteHtml) return '';

          return `
            <div class="product-information__media-caption-note">
              <p class="product-information__media-caption-note-label">${this.escapeHtml(label)}</p>
              <div class="product-information__media-caption-note-value">${noteHtml}</div>
            </div>
          `;
        }

        /**
         * @param {ScentChoice | null} [visualScent]
         */
        renderMediaGalleryCaption(visualScent = this.getVisualScent()) {
          const caption = this.mediaGalleryCaption;
          if (
            !caption
            || !this.mediaGalleryCaptionTitle
            || !this.mediaGalleryCaptionTitleText
            || !this.mediaGalleryCaptionDescription
          ) {
            return;
          }

          const title = String(visualScent?.title || '').trim();
          const descriptionHtml = String(visualScent?.descriptionHtml || '').trim();
          const notesHtml = [
            this.renderMediaGalleryCaptionNote(cfg.topNotesLabel, visualScent?.topNotesHtml),
            this.renderMediaGalleryCaptionNote(cfg.middleNotesLabel, visualScent?.middleNotesHtml),
            this.renderMediaGalleryCaptionNote(cfg.baseNotesLabel, visualScent?.baseNotesHtml)
          ].filter(Boolean).join('');
          const hasContent = Boolean(title || descriptionHtml || notesHtml);

          // Called on every tap, usually with the scent already shown; rewriting identical
          // HTML still costs a style and layout pass.
          const captionKey = [title, descriptionHtml, notesHtml].join('\u0000');
          if (captionKey === this._captionKey) return;
          this._captionKey = captionKey;

          caption.hidden = !hasContent;
          if (this.mediaGalleryCaptionHeader) {
            this.mediaGalleryCaptionHeader.hidden = !title;
          }
          this.mediaGalleryCaptionTitle.hidden = !title;
          this.mediaGalleryCaptionDescription.hidden = !descriptionHtml;
          this.mediaGalleryCaptionTitleText.textContent = title;
          this.mediaGalleryCaptionDescription.innerHTML = descriptionHtml;
          if (this.mediaGalleryCaptionNotes) {
            this.mediaGalleryCaptionNotes.hidden = !notesHtml;
            this.mediaGalleryCaptionNotes.innerHTML = notesHtml;
          }

          if (hasContent) {
            requestAnimationFrame(() => {
              const currentHeight = caption.offsetHeight;
              const prevMin = this._captionMaxHeight || 0;
              if (currentHeight > prevMin) {
                this._captionMaxHeight = currentHeight;
                caption.style.setProperty('--media-caption-min-height', `${currentHeight}px`);
              }
            });
          }
        }

        syncProductInformationBackground() {
          const visualScent = this.getVisualScent();
          this.setProductInformationBackground(visualScent?.scentColor || '');
          this.renderMediaGalleryCaption(visualScent);
          this.syncRefillableHeroMedia(visualScent);
        }

        /**
         * @param {string | null} scentKey
         */
        previewScent(scentKey) {
          const nextScentKey = scentKey ? String(scentKey) : null;
          if (this.state.lastInteractedScentKey === nextScentKey) return;

          this.state.lastInteractedScentKey = nextScentKey;
          this.renderScents();
          this.syncProductInformationBackground();
          this.emitStateChange();
        }

        renderModeOptions() {
          this.modeButtons.forEach((button) => {
            const isActive = button.dataset.mode === this.state.mode;
            button.classList.toggle('is-active', isActive);
            setAttr(button, 'aria-pressed', isActive ? 'true' : 'false');
          });
          this.syncRouteClasses();
        }

        // Lets neighbouring theme blocks follow the chosen route without any wiring of their
        // own: a block given the custom CSS class `refillable-starter-only` is hidden on the
        // refill route, `refillable-refill-only` on the Starter Pack route (see the snippet's
        // stylesheet). The classes sit on the product details container the blocks share.
        syncRouteClasses() {
          const root = this.closest('.product-details') || this.closest('.shopify-section') || this.parentElement;
          if (!root) return;
          const isRefill = this.state.mode === 'refill';
          root.classList.toggle('refillable-mode--refill', isRefill);
          root.classList.toggle('refillable-mode--starter', !isRefill);
        }

        renderPlanOptions() {
          findAll(this, '[data-plan]').forEach((button) => {
            const isActive = button.dataset.plan === this.state.plan;
            button.classList.toggle('is-active', isActive);
            setAttr(button, 'aria-pressed', isActive ? 'true' : 'false');
          });

          findAll(this, '[data-plan-card]').forEach((card) => {
            card.classList.toggle('is-active', card.dataset.planCard === this.state.plan);
          });

          this.renderPlanSubscriptionTitle();
          this.renderPlanSentence();
        }

        /**
         * '' unless a one-off order with a scent in it is short of its minimum.
         * @param {SelectedScent[]} selectedScents
         */
        getOnetimeHint(selectedScents) {
          const rule = cfg.onetimeMinimum?.[this.state.mode];
          if (this.state.plan !== 'onetime' || !rule || !selectedScents.length) return '';
          const shortfall = rule.minimum - selectedScents.reduce((total, scent) => total + scent.quantity, 0);
          return shortfall > 0 ? rule.hints[Math.min(shortfall, rule.hints.length) - 1] || '' : '';
        }

        renderFrequencyOptions() {
          if (this.frequencySelect) {
            this.frequencySelect.value = this.state.frequency;
          }

          this.renderPlanSentence();
        }

        // The sentence reads "every 3 months", so the select supplies only the "3 months" core.
        // Same extraction the desc template has always used; it holds for en/de/es/fr/nl.
        shortenFrequencyOptions() {
          if (!this.frequencySelect) return;

          Array.from(this.frequencySelect.options).forEach((option) => {
            const core = option.textContent?.trim().match(/(\d+\s+\w+)/)?.[1];
            if (core) option.textContent = core.trim();
          });
        }

        getFrequencyLabel() {
          const option = find(this.frequencySelect, `option[value="${this.state.frequency}"]`);
          return option?.textContent?.trim() || '';
        }

        /**
         * @param {boolean} open
         */
        togglePlanInfo(open) {
          if (this.planInfo) setAttr(this.planInfo, 'aria-expanded', open ? 'true' : 'false');
        }

        /**
         * @param {string} frequency
         * @param {boolean} [useStarterPack]
         */
        getSellingPlanId(frequency, useStarterPack = false) {
          const source = useStarterPack
            ? (this.data?.starterPackSellingPlans || [])
            : (this.data?.sellingPlans || []);
          const plans = source.length ? source : (this.data?.sellingPlans || []);
          if (!plans.length) return null;
          const n = Number(frequency);
          /** @param {unknown} v */
          const isMonth = (v) => typeof v === 'string' && v.toLowerCase() === 'month';
          /** @param {string} name */
          const nameHasFreq = (name) => {
            const m = name && name.match(/every\s+(\d+)\s+month/i);
            return m ? Number(m[1]) : null;
          };
          const plan =
            plans.find((p) => isMonth(p.interval) && Number(p.intervalCount) === n) ||
            plans.find((p) => isMonth(p.deliveryInterval) && Number(p.deliveryIntervalCount) === n) ||
            plans.find((p) => nameHasFreq(p.name) === n) ||
            plans[0];
          return plan ? plan.id : null;
        }

        autoSelectSubscriptionScent() {
          if (this.state.plan !== 'subscription') return;

          const selected = this.getSelectedScents();
          const [first] = selected;

          if (!first) {
            this.state.subscriptionScentKey = null;
            this.autoSelectStarterPackScent();
            return;
          }

          if (selected.length === 1) {
            this.state.subscriptionScentKey = first.key;
            this.autoSelectStarterPackScent();
            return;
          }

          const stillSelected = selected.some((s) => s.key === this.state.subscriptionScentKey);
          if (!stillSelected) {
            this.state.subscriptionScentKey = first.key;
          }
          this.autoSelectStarterPackScent();
        }

        // The visible text lives in the label, not the select, so the slot sizes to the selected
        // option instead of the widest one.
        /**
         * @param {HTMLElement | null} slot
         * @param {HTMLSelectElement | null} select
         */
        syncSlotLabel(slot, select) {
          const label = find(slot, '[data-plan-slot-label]');
          if (!label || !select) return;
          setText(label, select.options[select.selectedIndex]?.textContent || '');
        }

        getSubscriptionScentTitle() {
          const selected = this.getSelectedScents();
          const current = selected.find((scent) => scent.key === this.state.subscriptionScentKey);
          return (current || selected[0])?.title || '';
        }

        syncSubscriptionScentOptions() {
          const select = this.subscriptionScentSelect;
          if (!select) return;

          const currentKey = this.state.subscriptionScentKey;
          select.replaceChildren(...this.getSelectedScents().map((scent) => {
            const option = document.createElement('option');
            option.value = scent.key;
            option.textContent = scent.colourTitle || scent.title;
            option.selected = scent.key === currentKey;
            return option;
          }));
        }

        // Rebuilds the plan sentence from the locale template by re-parenting the existing slot
        // nodes, so __SCENT__ / __FREQUENCY__ can sit in any order per language and the selects
        // keep their listeners. Renders as flat text unless the subscription plan is selected.
        renderPlanSentence() {
          const planSentence = this.planSentence;
          if (!planSentence) return;

          const isSubscription = this.state.plan === 'subscription';
          const selectedScents = this.getSelectedScents();
          const showScent = selectedScents.length > 1;
          const frequencyLabel = this.getFrequencyLabel();
          const sentenceTemplate = this.getPlanSubscriptionSentenceTemplate();

          this.togglePlanInfo(false);

          // Most taps leave the sentence as it was; rebuilding it recreates the scent options
          // and re-parents both selects.
          const sentenceKey = JSON.stringify([
            isSubscription,
            this.state.mode,
            frequencyLabel,
            this.state.frequency,
            this.state.subscriptionScentKey,
            selectedScents.map((scent) => [scent.key, scent.colourTitle || scent.title])
          ]);
          if (sentenceKey === this._planSentenceKey) return;
          this._planSentenceKey = sentenceKey;

          // Capture before clearing: changing a slot re-renders the sentence, and re-parenting a
          // focused select would otherwise drop focus mid-interaction.
          const active = document.activeElement;
          const focused = active instanceof HTMLElement && planSentence.contains(active) ? active : null;

          // Clear before bailing: the slots are authored in Liquid, so a missing template would
          // otherwise leave bare dropdowns sitting there with no sentence around them.
          planSentence.replaceChildren();
          if (!sentenceTemplate) return;

          this.syncSubscriptionScentOptions();
          if (this.frequencySelect) this.frequencySelect.value = this.state.frequency;
          this.syncSlotLabel(this.planScentSlot, this.subscriptionScentSelect);
          this.syncSlotLabel(this.planFrequencySlot, this.frequencySelect);

          /** @param {Node} node */
          const append = (node) => planSentence.appendChild(node);
          /** @param {string} text */
          const appendText = (text) => {
            if (text) append(document.createTextNode(text));
          };

          const appendScent = () => {
            if (!showScent) return;

            const clause = subscriptionScentClause || '__SCENT__';
            clause.split(/(__SCENT__)/).forEach((chunk) => {
              if (chunk !== '__SCENT__') return appendText(chunk);
              if (isSubscription && this.planScentSlot) append(this.planScentSlot);
              else appendText(this.getSubscriptionScentTitle());
            });
          };

          sentenceTemplate.split(/(__SCENT__|__FREQUENCY__)/).forEach((chunk) => {
            if (chunk === '__SCENT__') return appendScent();
            if (chunk !== '__FREQUENCY__') return appendText(chunk);
            if (isSubscription && this.planFrequencySlot) append(this.planFrequencySlot);
            else appendText(frequencyLabel);
          });

          if (isSubscription && showScent && this.planInfo) {
            appendText(' ');
            append(this.planInfo);
          }

          if (focused && planSentence.contains(focused)) focused.focus();
        }

        /**
         * @param {string} template
         * @param {number} stepNumber
         */
        formatStepNumberLabel(template, stepNumber) {
          const normalizedTemplate = String(template || '').trim();

          if (!normalizedTemplate) {
            return String(stepNumber);
          }

          const match = normalizedTemplate.match(/\d+/);
          if (!match) {
            return `${normalizedTemplate} ${stepNumber}`;
          }

          const currentNumber = match[0];
          const index = match.index ?? 0;
          const replacement = String(stepNumber).padStart(currentNumber.length, '0');

          return `${normalizedTemplate.slice(0, index)}${replacement}${normalizedTemplate.slice(index + currentNumber.length)}`;
        }

        syncStepNumbers() {
          this.stepSections
            .filter((step) => !step.hidden)
            .forEach((step, index) => {
              const stepNumber = find(step, '[data-step-number]');
              if (!stepNumber) return;

              const template = stepNumber.dataset.stepNumberTemplate || stepNumber.textContent || '';
              setText(stepNumber, this.formatStepNumberLabel(template, index + 1));
            });
        }

        /**
         * @param {BuilderMode} mode
         */
        setMode(mode) {
          const nextStep = mode === 'starter' && !this.scentsFirst ? this.caseStep : this.scentsStep;
          // The opening kit is already chosen, so shoppers tap it expecting to move on.
          if (mode === this.state.mode) {
            this.scrollToStep(nextStep);
            return;
          }

          this.state.mode = mode;
          this.state.caseVariantId = mode === 'starter' ? this.getDefaultCaseKey() : null;
          this.state.hasInteractedWithCaseVariant = false;
          this.state.plan = 'subscription';
          this.state.frequency = '3';
          this.state.scentQuantities = {};
          this.state.subscriptionScentKey = null;
          this.state.starterPackScentKey = null;
          this.state.lastInteractedScentKey = this.getDefaultScentKey();
          this.renderModeOptions();
          this.renderPlanOptions();
          this.renderFrequencyOptions();
          this.renderStepTwoOptions();
          this.renderScents();
          this.orderScentCards();
          this.syncPlanSteps();
          this.setStatus('');
          this.renderSummary();
          this.emitStateChange();
          this.scrollToStep(nextStep);
        }

        /**
         * @param {'subscription' | 'onetime'} plan
         */
        setPlan(plan) {
          this.state.plan = plan;
          if (plan !== 'subscription') {
            this.state.frequency = '3';
            this.state.subscriptionScentKey = null;
          } else {
            this.autoSelectSubscriptionScent();
          }
          this.renderPlanOptions();
          this.renderFrequencyOptions();
          this.syncPlanSteps();
          this.setStatus('');
          this.renderSummary();
          this.emitStateChange();
        }

        /**
         * @param {string} frequency
         */
        setFrequency(frequency) {
          this.state.frequency = frequency;
          this.renderFrequencyOptions();
          this.renderSummary();
          this.emitStateChange();
        }

        /**
         * @param {HTMLElement | null} stepEl
         * @param {boolean} [always] - Scroll even when step autoscroll is switched off.
         */
        scrollToStep(stepEl, always = false) {
          if (!stepEl || (!always && this.dataset.stepAutoscroll !== 'true')) return;
          setTimeout(() => {
            const stickyOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
            const rect = stepEl.getBoundingClientRect();
            const scrollTop = window.scrollY + rect.top - stickyOffset - 16;
            window.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
          }, 200);
        }

        // The swatches are rendered by Liquid; this only marks the selected one.
        renderStepTwoOptions() {
          const isStarter = this.state.mode === 'starter';
          setHidden(this.caseStep, !isStarter);

          if (isStarter && !this.isValidCaseKey(this.state.caseVariantId, 'starter')) {
            this.state.caseVariantId = this.getDefaultCaseKey();
          }

          findAll(this.caseOptions, '[data-step-two-option]').forEach((button) => {
            const isActive = isStarter && button.dataset.stepTwoOption === String(this.state.caseVariantId);
            button.classList.toggle('is-active', isActive);
            setAttr(button, 'aria-pressed', isActive ? 'true' : 'false');
          });

          this.syncPlanSteps();
        }

        /**
         * @param {string} caseKey
         */
        selectCaseOption(caseKey) {
          this.state.hasInteractedWithCaseVariant = true;
          this.state.caseVariantId = caseKey;
          this.renderStepTwoOptions();
          this.renderScents();
          this.orderScentCards();
          this.syncProductInformationBackground();
          this.renderSummary();
          this.emitStateChange();
          this.scrollToStep(this.scentsFirst ? this.planStep : this.scentsStep);
        }

        /**
         * @param {string} scentKey
         */
        isScentAvailableViaStarterPack(scentKey) {
          if (!this.hasStarterPack || this.state.mode !== 'starter') return false;
          const caseKey = this.state.caseVariantId;
          if (!caseKey) return false;
          // Once a scent opens the pack, every other pick can only be a refill.
          if (this.state.starterPackScentKey && Object.values(this.state.scentQuantities).some((q) => Number(q) > 0)) return false;

          return this.getStarterPackVariants().some((v) => {
            return this.getCaseSlotKey(v) === caseKey
              && this.getScentSlotKey(v) === scentKey
              && v.available;
          });
        }

        // In case + refills, a scent with no starter-pack variant is hidden outright rather
        // than shown disabled: it cannot open a bundle, and the merchant chose not to offer
        // it as a ride-along there either.
        /**
         * @returns {ScentChoice[]}
         */
        getScentChoices() {
          return this.getAllScentChoices().filter((choice) => !choice.refillOnly);
        }

        /**
         * @returns {ScentChoice[]}
         */
        getAllScentChoices() {
          const refills = this.buildVariantMapByScent(this.data.refillProduct.variants, this.refillScentIndex);
          const noSprays = this.buildVariantMapByScent(this.data.noSprayProduct?.variants || [], this.noSprayScentIndex);
          const isStarterPackMode = this.hasStarterPack && this.state.mode === 'starter';
          const hasAnySelection = Object.values(this.state.scentQuantities).some((q) => Number(q) > 0);

          return Array.from(refills.values()).map((variant) => {
            const scentKey = variant.scentKey;
            const noSpray = noSprays.get(scentKey);
            const availableNoSpray = noSpray?.available && noSpray.id ? noSpray : null;
            const refillAvailable = Boolean(variant.available);
            const starterEligible = !isStarterPackMode || this.isStarterEligible(scentKey);
            let scentAvailable;
            if (isStarterPackMode && !starterEligible) {
              scentAvailable = refillAvailable && this.hasStarterEligibleSelection();
            } else if (isStarterPackMode && !hasAnySelection) {
              scentAvailable = this.isScentAvailableViaStarterPack(scentKey);
            } else if (isStarterPackMode) {
              scentAvailable = refillAvailable || this.isScentAvailableViaStarterPack(scentKey);
            } else {
              scentAvailable = refillAvailable;
            }

            return {
              key: scentKey,
              title: variant.displayTitle || variant.scentValue || variant.title,
              colourTitle: variant.colourTitle || variant.scentValue || variant.title,
              ...this.getScentCardPresentation(variant),
              scentColor: variant.scentColor || '',
              variantId: variant.id,
              available: scentAvailable,
              refillAvailable: refillAvailable,
              refillOnly: isStarterPackMode && !starterEligible,
              price: variant.price || 0,
              image: variant.swatchImage || variant.image || null,
              descriptionHtml: variant.descriptionHtml || '',
              topNotesHtml: variant.topNotesHtml || '',
              middleNotesHtml: variant.middleNotesHtml || '',
              baseNotesHtml: variant.baseNotesHtml || '',
              heroRefillLayer: variant.heroRefillLayer || null,
              noSprayVariantId: availableNoSpray?.id ?? null,
              noSprayPrice: availableNoSpray?.price ?? null
            };
          });
        }

        // The variant a card's next tap adds, as buildSelectedItems adds it: the starter pack before
        // the first pick in Case + Refills; then each scent's first refill with spray; after that
        // refills without spray, or the spray refill when that one is out or doesn't exist. Its
        // stock decides the card's "+" and what Notify me signs up for. Names match the product
        // page's button: product – variant.
        /**
         * @param {ScentChoice} scent
         * @returns {{ variant: BuilderVariant, name: string } | null}
         */
        getNextUnit(scent) {
          const isStarter = this.hasStarterPack && this.state.mode === 'starter';
          const quantity = this.getScentQuantity(scent.key);
          if (isStarter && !Object.values(this.state.scentQuantities).some((q) => Number(q) > 0)) {
            const pack = this.getStarterPackVariantForSelection(this.state.caseVariantId, scent.key);
            const title = this.data.starterPackProduct?.title;
            return pack && title ? { variant: pack, name: `${title} – ${pack.title}` } : null;
          }

          const spray = this.getRefillVariantById(scent.variantId);
          if (!spray) return null;
          const sprayUnit = { variant: spray, name: `${this.data.refillProduct.title} – ${spray.title}` };
          const isPrimary = isStarter && this.state.starterPackScentKey === scent.key;
          if ((isPrimary ? quantity - 1 : quantity) <= 0) return sprayUnit;

          const noSpray = this.data.noSprayProduct?.variants.find((variant) => this.getScentSlotKey(variant) === scent.key);
          if (!noSpray || (!noSpray.available && spray.available)) return sprayUnit;
          return { variant: noSpray, name: `${this.data.noSprayProduct?.title} – ${noSpray.title}` };
        }

        // Sold-out scents go after the ones you can buy, as Liquid renders them. Called on case
        // and mode changes only: reordering while scents are being picked would move cards
        // under the shopper's finger.
        orderScentCards() {
          const cards = findAll(this.scentGrid, '[data-scent-card]');
          const slot = (/** @type {HTMLElement} */ card) => Number((card.dataset.scentCard || '').slice(1)) || 0;
          const soldOut = (/** @type {HTMLElement} */ card) =>
            card.classList.contains('is-unavailable') && !card.classList.contains('is-selected');
          const ordered = [...cards].sort((a, b) => Number(soldOut(a)) - Number(soldOut(b)) || slot(a) - slot(b));
          if (ordered.every((card, index) => card === cards[index])) return;

          const active = document.activeElement;
          ordered.forEach((card) => this.scentGrid.append(card));
          if (active instanceof HTMLElement && active !== document.activeElement) active.focus({ preventScroll: true });
        }

        // Liquid renders every card with every control it can show, so this is the only place
        // the cards change after load: it writes the current state onto them.
        renderScents() {
          // Which scent is the starter pack's decides each card's next unit, so settle it first.
          if (this.hasStarterPack && this.state.mode === 'starter') this.autoSelectStarterPackScent();
          const scents = new Map(this.getAllScentChoices().map((scent) => [String(scent.key), scent]));

          findAll(this.scentGrid, '[data-scent-card]').forEach((card) => {
            const scent = scents.get(card.dataset.scentCard || '');
            setHidden(card, !scent || Boolean(scent.refillOnly));
            if (!scent) return;

            const quantity = this.getScentQuantity(scent.key);
            const isAvailable = Boolean(scent.available && scent.variantId);
            const hasQuantity = quantity > 0;
            const showSoldOut = !isAvailable && !hasQuantity;

            card.classList.toggle('is-unavailable', !isAvailable);
            card.classList.toggle('is-selected', hasQuantity);
            card.classList.toggle('is-previewed', String(this.state.lastInteractedScentKey) === String(scent.key));

            // Badges come in two versions; the attribute records which one is showing.
            const badgesEl = find(card, '[data-scent-badges]');
            if (badgesEl) {
              if (badgesEl.dataset.scentBadges !== scent.badgesVersion) {
                badgesEl.innerHTML = scent.badgesHtml;
                badgesEl.dataset.scentBadges = scent.badgesVersion;
              }
              setHidden(badgesEl, !scent.badgesHtml);
            }
            const atMax = quantity >= MAX_PER_SCENT;
            const maxNoteEl = find(card, '[data-scent-max-note]');
            if (maxNoteEl) setHidden(maxNoteEl, !atMax);
            const preorderNoteEl = find(card, '[data-scent-preorder-note]');
            if (preorderNoteEl) {
              if (scent.preorderNoteHtml && preorderNoteEl.hidden) preorderNoteEl.innerHTML = scent.preorderNoteHtml;
              setHidden(preorderNoteEl, !scent.preorderNoteHtml);
            }

            const actionsEl = find(card, '.refillable-builder__scent-actions');
            if (!actionsEl) return;

            // ADD and the quantity stepper swap places via `hidden`. Whichever one the user
            // just activated is about to become display:none while it still holds focus, which
            // blurs them to <body> and sends the next Tab back to the top of the page.
            const active = document.activeElement;
            const focusedBefore = active instanceof HTMLElement && actionsEl.contains(active) ? active : null;

            actionsEl.classList.toggle('is-sold-out', showSoldOut);
            /** @type {HTMLButtonElement | null} */
            const addBtn = find(actionsEl, '[data-scent-add]');
            if (addBtn) {
              setHidden(addBtn, !isAvailable || hasQuantity);
              setDisabled(addBtn, !isAvailable);
            }
            const soldOutLabel = find(actionsEl, '.refillable-builder__scent-stock-label');
            if (soldOutLabel) setHidden(soldOutLabel, !showSoldOut);

            const next = showSoldOut || hasQuantity ? this.getNextUnit(scent) : null;
            const nextOut = Boolean(next && !next.variant.available);
            /** @param {HTMLElement} button */
            const pointNotify = (button) => {
              if (!next) return;
              setAttr(button, 'data-notify-me', String(next.variant.id));
              setAttr(button, 'data-notify-me-name', next.name);
            };

            const notifyWrap = find(actionsEl, '[data-scent-notify]');
            const notifyBtn = notifyWrap && find(notifyWrap, '[data-notify-me]');
            if (notifyWrap && notifyBtn) {
              const show = showSoldOut && nextOut;
              if (show) pointNotify(notifyBtn);
              setHidden(notifyWrap, !show);
            }

            const qtyWrap = find(actionsEl, '[data-scent-qty]');
            if (qtyWrap) {
              setHidden(qtyWrap, !hasQuantity);
              /** @type {HTMLInputElement | null} */
              const qtyInput = find(qtyWrap, 'input');
              if (qtyInput && qtyInput.value !== String(quantity)) qtyInput.value = String(quantity);
              /** @type {HTMLButtonElement | null} */
              const plusBtn = find(qtyWrap, '[data-delta="1"]');
              const qtyNotify = find(qtyWrap, '.refillable-builder__qty-notify');
              const moreOut = hasQuantity && nextOut && !atMax;
              if (plusBtn) {
                setDisabled(plusBtn, hasQuantity ? atMax || !next?.variant.available : !isAvailable);
                setHidden(plusBtn, Boolean(qtyNotify) && moreOut);
              }
              if (qtyNotify) {
                if (moreOut) pointNotify(qtyNotify);
                setHidden(qtyNotify, !moreOut);
              }
              this.updateScentQtyMinusButton(qtyWrap, quantity);
            }

            // `closest` covers both cases: ADD hides itself, the stepper buttons are hidden by
            // their wrapper. A control can also be disabled while still on screen, which blurs
            // it just the same. Hand focus to whichever control replaced the one that vanished.
            const isDisabled = focusedBefore instanceof HTMLButtonElement && focusedBefore.disabled;
            if (focusedBefore && (focusedBefore.closest('[hidden]') || isDisabled)) {
              const successor = (hasQuantity
                ? find(qtyWrap, '[data-delta="1"]:not([disabled])') || find(qtyWrap, '.refillable-builder__qty-notify:not([hidden])')
                : find(actionsEl, '[data-scent-add]:not([disabled])'))
                || find(actionsEl, '[data-scent-notify]:not([hidden]) [data-notify-me]')
                || find(card, '[data-scent-info-trigger]:not([disabled])');
              if (successor) {
                successor.focus({ preventScroll: true });
              } else {
                // The scent just went unavailable, so nothing in the card is focusable. Park
                // focus on the card itself, or the next Tab restarts at the top of the page.
                card.tabIndex = -1;
                card.focus({ preventScroll: true });
              }
            }
          });
        }

        /**
         * @param {MouseEvent} event
         */
        handleScentGridClick(event) {
          const target = event.target instanceof Element ? event.target : null;
          if (!target) return;

          // The "i" opens the scent popup through its own on:click; it must not add the scent.
          // Notify me likewise opens its form from a document listener (notify-me.js).
          if (closest(target, '[data-scent-info-trigger]') || closest(target, '[data-notify-me]')) return;

          const addKey = closest(target, '[data-scent-add]')?.dataset.scentAdd;
          if (addKey) {
            this.updateScentQuantity(addKey, 1);
            return;
          }
          const changeBtn = closest(target, '[data-scent-change]');
          const changeKey = changeBtn?.dataset.scentChange;
          if (changeBtn && changeKey) {
            this.updateScentQuantity(changeKey, this.getScentQuantity(changeKey) + Number(changeBtn.dataset.delta || 0));
            return;
          }
          if (target.closest('[data-scent-qty]')) return;

          const card = closest(target, '[data-scent-card]');
          const scentKey = card?.dataset.scentCard;
          if (!card || !scentKey) return;

          // Once a scent is in the selection the card is preview-only: removal goes through
          // the bin button in the quantity control, so a stray tap can't wipe a chosen refill.
          if (this.getScentQuantity(scentKey) > 0) {
            this.previewScent(scentKey);
            return;
          }

          // Shoppers tap sold-out cards too, so the whole card opens its Notify me.
          if (card.classList.contains('is-unavailable')) {
            /** @type {HTMLElement | null} */ (find(card, '[data-scent-notify]:not([hidden]) [data-notify-me]'))?.click();
            return;
          }
          this.updateScentQuantity(scentKey, 1);
          this.syncProductInformationBackground();
        }

        /**
         * @param {string} variantId
         * @param {number} quantity
         */
        updateScentQuantity(variantId, quantity) {
          const nextQuantity = Math.min(MAX_PER_SCENT, Math.max(0, quantity));
          if (nextQuantity === 0) {
            delete this.state.scentQuantities[String(variantId)];
            if (String(this.state.lastInteractedScentKey) === String(variantId)) {
              this.state.lastInteractedScentKey = null;
            }
          } else {
            this.state.scentQuantities[String(variantId)] = nextQuantity;
            this.state.lastInteractedScentKey = String(variantId);
          }
          this.dropStrandedScents();
          this.autoSelectSubscriptionScent();
          this.renderScents();
          this.renderPlanSentence();
          this.renderSummary();
          this.emitStateChange();
        }

        // In starter mode the bundle needs an eligible scent. Once the last one is removed the
        // rest cannot be bought on their own, so they go too.
        dropStrandedScents() {
          if (!this.hasStarterPack || this.state.mode !== 'starter') return;
          if (this.hasStarterEligibleSelection()) return;
          Object.keys(this.state.scentQuantities).forEach((key) => {
            delete this.state.scentQuantities[key];
          });
          this.state.lastInteractedScentKey = null;
          this.state.starterPackScentKey = null;
        }

        /**
         * @param {string} variantId
         */
        getScentQuantity(variantId) {
          return Number(this.state.scentQuantities[String(variantId)] || 0);
        }

        syncPlanSteps() {
          setHidden(this.planStep, false);
          this.syncStepNumbers();
        }

        getSelectedBaseVariantId() {
          if (this.state.mode !== 'starter') return null;
          if (this.hasStarterPack) {
            const spv = this.getSelectedStarterPackVariant();
            return spv ? String(spv.id) : null;
          }
          return this.state.caseVariantId || this.getDefaultCaseVariantId();
        }

        /**
         * @returns {SelectedScent[]}
         */
        getSelectedScents() {
          return this.getScentChoices()
            .filter((scent) => this.getScentQuantity(scent.key) > 0 && scent.variantId)
            .map((scent) => ({
              ...scent,
              quantity: this.getScentQuantity(scent.key)
            }));
        }

        /**
         * @param {SelectedScent[]} selectedScents
         */
        getSelectedScentsTotalPrice(selectedScents) {
          const isStarterPack = this.hasStarterPack && this.state.mode === 'starter';
          const primaryScentKey = isStarterPack ? this.state.starterPackScentKey : null;
          let primaryDeducted = false;

          return selectedScents.reduce((sum, scent) => {
            let effectiveQty = scent.quantity;

            if (isStarterPack && !primaryDeducted && primaryScentKey) {
              if (scent.key === primaryScentKey) {
                effectiveQty = Math.max(0, scent.quantity - 1);
                primaryDeducted = true;
              }
            }

            if (effectiveQty === 0) return sum;

            if (this.usesAutomaticNoSpraySplit) {
              const sprayPrice = Number(scent.price || 0);
              const noSprayPrice = scent.noSprayPrice != null ? Number(scent.noSprayPrice) : sprayPrice;
              const extraNoSpray = Math.max(0, effectiveQty - 1);
              return sum + sprayPrice + (noSprayPrice * extraNoSpray);
            }

            return sum + (Number(scent.price || 0) * effectiveQty);
          }, 0);
        }

        /**
         * @param {SelectedScent[]} selectedScents
         */
        formatSelectedScentsSummary(selectedScents) {
          if (!selectedScents.length) {
            return refillableBuilderSummaryZeroLabel;
          }

          const totalItems = selectedScents.reduce((count, scent) => count + scent.quantity, 0);
          const scentBreakdown = selectedScents.map((scent) => {
            return `${scent.quantity} ${scent.colourTitle || scent.title}`;
          }).join(', ');
          const unitLabel = totalItems === 1
            ? refillableBuilderSummaryUnitSingular
            : refillableBuilderSummaryUnitPlural;

          return `${totalItems} ${unitLabel} (${scentBreakdown})`;
        }

        renderSummary() {
          const selectedScents = this.getSelectedScents();
          if (this.hasStarterPack && this.state.mode === 'starter') {
            this.autoSelectStarterPackScent();
          }
          const baseVariant = this.getSelectedBaseVariant();
          const starterPackVariant = this.getSelectedStarterPackVariant();
          const includeBasePrice = this.state.mode === 'starter';
          let baseCost = 0;
          if (includeBasePrice) {
            if (this.hasStarterPack) {
              baseCost = starterPackVariant ? Number(starterPackVariant.price || 0) : (this.data.starterPackProduct?.basePrice || 0);
            } else {
              baseCost = baseVariant ? Number(baseVariant.price || 0) : 0;
            }
          }
          const totalPrice = baseCost + this.getSelectedScentsTotalPrice(selectedScents);
          const subscriptionDiscountRate = this.getSubscriptionDiscountRate();
          const isSubscription = this.state.plan === 'subscription';
          let subscriptionPrice = totalPrice;
          if (subscriptionDiscountRate > 0) {
            if (this.hasStarterPack && this.state.mode === 'starter') {
              const spBasePrice = starterPackVariant ? Number(starterPackVariant.price || 0) : baseCost;
              const spDiscounted = Math.round(spBasePrice * (1 - subscriptionDiscountRate));
              subscriptionPrice = totalPrice - (spBasePrice - spDiscounted);
            } else {
              const subscribedScent = selectedScents.find((s) => s.key === this.state.subscriptionScentKey) || selectedScents[0];
              if (subscribedScent) {
                const scentFullPrice = Number(subscribedScent.price || 0);
                const scentDiscounted = Math.round(scentFullPrice * (1 - subscriptionDiscountRate));
                subscriptionPrice = totalPrice - (scentFullPrice - scentDiscounted);
              }
            }
          }
          subscriptionPrice = Math.round(subscriptionPrice / 100) * 100;
          const activePlanPrice = isSubscription ? subscriptionPrice : totalPrice;
          const modeLabel = this.state.mode === 'starter'
            ? this.dataset.productModeLabelStarter
            : this.dataset.productModeLabelRefill;
          let modeDetail = '';
          if (this.state.mode === 'starter') {
            const effectiveCase = this.getEffectiveCaseVariant();
            modeDetail = effectiveCase?.title ? `${effectiveCase.title} Case` : '';
          }
          const scentSummary = this.formatSelectedScentsSummary(selectedScents);
          const scentCount = selectedScents.reduce((total, scent) => total + scent.quantity, 0);

          if (this.incentiveText) {
            setText(this.incentiveText, scentCount === 0
              ? this.incentiveDefaultText
              : scentCount === 1
                ? bannerTextOneRefill
                : bannerTextTwoPlus);
          }

          // The line is clipped on a phone, so the prompt to pick a scent stands alone.
          setText(this.summary, selectedScents.length
            ? `${modeLabel}${modeDetail ? `, ${modeDetail}` : ''} · ${scentSummary}`
            : scentSummary);
          setText(this.total, activePlanPrice > 0 ? this.formatMoney(activePlanPrice) : '');
          if (this.ctaPriceNote) {
            setText(this.ctaPriceNote, (isSubscription
              ? this.ctaPriceNote.dataset.noteSubscribe
              : this.ctaPriceNote.dataset.noteOnetime) || '');
            // The note annotates the amount above it, so it has nothing to say while that is
            // blank — which is the state the bar loads in, before any scent is picked.
            setHidden(this.ctaPriceNote, activePlanPrice <= 0);
          }
          if (this.planPriceOneTime) {
            // Below the minimum this total can't be bought one-off, yet it would sit beside "min. 2 refills".
            const minimum = cfg.onetimeMinimum?.[this.state.mode]?.minimum || 1;
            const buyable = minimum < 2 || scentCount >= minimum;
            setText(this.planPriceOneTime, totalPrice > 0 && buyable ? this.formatMoney(totalPrice) : '');
          }
          if (this.planPriceSubscriptionCompare) {
            const showCompare = totalPrice > 0 && subscriptionPrice < totalPrice;
            setText(this.planPriceSubscriptionCompare, showCompare ? this.formatMoney(totalPrice) : '');
          }
          if (this.planPriceSubscriptionSale) {
            setText(this.planPriceSubscriptionSale, subscriptionPrice > 0 ? this.formatMoney(subscriptionPrice) : '');
          }
          this.syncProductInformationBackground();

          const hasPrimarySelection = this.state.mode === 'starter'
            ? (this.hasStarterPack ? Boolean(this.state.caseVariantId) : Boolean(baseVariant))
            : true;
          // Stays enabled while submitting, like Horizon's add-to-cart, so the checkmark isn't dimmed;
          // submitSelection ignores repeat clicks.
          const onetimeHint = this.getOnetimeHint(selectedScents);
          const canSubmit = hasPrimarySelection && selectedScents.length > 0 && !onetimeHint;
          // aria-disabled rather than the native attribute: a natively disabled button drops out
          // of the tab order, so a keyboard user never finds add-to-cart or learns what it needs.
          setAttr(this.submitButton, 'aria-disabled', canSubmit ? 'false' : 'true');
          if (this.submitHint) {
            setText(this.submitHint, selectedScents.length === 0 ? (cfg.submitRequiresScent || '') : onetimeHint);
          }
          if (onetimeHint !== this.onetimeHint) {
            this.setStatus(onetimeHint);
            this.onetimeHint = onetimeHint;
          }
        }

        getSelectedBaseVariant() {
          if (this.state.mode === 'refill') return null;
          if (this.hasStarterPack) return this.getSelectedStarterPackVariant();
          return this.getCaseVariantById(this.getSelectedBaseVariantId());
        }

        /**
         * @param {number} cents
         */
        formatMoney(cents) {
          return formatShopMoney(cents, this.moneyFormat, this.currency);
        }

        /**
         * @param {unknown} value
         */
        escapeHtml(value) {
          return String(value || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
        }

        /**
         * @param {string} message
         * @param {'error'} [tone]
         */
        setStatus(message, tone) {
          this.onetimeHint = '';
          // Failures go to the assertive region so a blocked add-to-cart interrupts rather than
          // queueing politely behind whatever else is being announced.
          const target = tone === 'error' && this.statusError ? this.statusError : this.status;
          const other = target === this.status ? this.statusError : this.status;
          if (other) {
            other.textContent = '';
            other.removeAttribute('data-tone');
          }
          target.textContent = message || '';
          if (tone) {
            target.dataset.tone = tone;
          } else {
            target.removeAttribute('data-tone');
          }
        }

        /**
         * @returns {CartLine[]}
         */
        buildItems() {
          const items = this.buildSelectedItems();
          const variants = new Map([
            ...(this.data?.starterPackProduct?.variants || []),
            ...(this.data?.caseProduct?.variants || []),
            ...(this.data?.refillProduct?.variants || []),
            ...(this.data?.noSprayProduct?.variants || [])
          ].map((variant) => [String(variant.id), variant]));
          for (const item of items) {
            const variant = variants.get(String(item.id));
            if (!variant?.available) throw new Error(cfg.soldOutLabel);
          }
          return items;
        }

        /**
         * @returns {CartLine[]}
         */
        buildSelectedItems() {
          const selectedScents = this.getSelectedScents();

          if (!selectedScents.length) {
            throw new Error(cfg.errorNoScent);
          }

          const isStarterPack = this.hasStarterPack && this.state.mode === 'starter';
          const sellingPlanId = this.state.plan === 'subscription' ? this.getSellingPlanId(this.state.frequency, isStarterPack) : null;
          const split = this.usesAutomaticNoSpraySplit;

          /** @type {CartLine[]} */
          const items = [];
          /** @type {string | null} */
          let primaryScentKey = null;

          if (isStarterPack) {
            this.autoSelectStarterPackScent();
            const starterPackVariant = this.getSelectedStarterPackVariant();
            if (!starterPackVariant) {
              throw new Error(cfg.errorNoStarterVariant);
            }

            /** @type {CartLine} */
            const starterItem = {
              id: Number(starterPackVariant.id),
              quantity: 1
            };
            if (sellingPlanId) {
              starterItem.selling_plan = sellingPlanId;
            }
            items.push(starterItem);
            primaryScentKey = this.state.starterPackScentKey;
          } else if (this.state.mode === 'starter') {
            const baseVariantId = this.getSelectedBaseVariantId();
            if (!baseVariantId) {
              throw new Error(cfg.errorNoStepTwo);
            }
            items.push({
              id: Number(baseVariantId),
              quantity: 1
            });
          }

          // The pack holds one refill of its scent already. With the no-spray split, each
          // scent's first refill comes with a spray and the rest without.
          selectedScents.forEach((scent) => {
            let quantity = scent.quantity;
            if (scent.key === primaryScentKey) {
              quantity -= 1;
              primaryScentKey = null;
            }
            if (quantity <= 0) return;

            /** @type {CartLine} */
            const item = {
              id: Number(scent.variantId),
              quantity: split ? 1 : quantity
            };
            if (!isStarterPack && sellingPlanId && scent.key === this.state.subscriptionScentKey) {
              item.selling_plan = sellingPlanId;
            }
            items.push(item);

            if (split && quantity > 1) {
              items.push({
                id: Number(scent.noSprayVariantId || scent.variantId),
                quantity: quantity - 1
              });
            }
          });

          return items;
        }

        openCartDrawerIfEnabled() {
          /** @type {(HTMLElement & { open?: () => void }) | null} */
          const cartDrawer = find(document, 'cart-drawer-component[auto-open]');
          if (typeof cartDrawer?.open !== 'function') return;

          cartDrawer.open();
        }

        async submitSelection() {
          if (this.isSubmitting) return;

          // The button only looks disabled, so a tap with nothing picked lands here. The scents
          // can be a screen or more away on a phone, so take the shopper there.
          if (!this.getSelectedScents().length) {
            this.setStatus(cfg.errorNoScent, 'error');
            this.scrollToStep(this.scentsStep, true);
            return;
          }

          const onetimeHint = this.getOnetimeHint(this.getSelectedScents());
          if (onetimeHint) {
            this.setStatus(onetimeHint, 'error');
            this.onetimeHint = onetimeHint;
            this.scrollToStep(this.scentsStep, true);
            return;
          }

          try {
            const items = this.buildItems();
            const addedItemCount = items.reduce((count, item) => count + Number(item.quantity || 0), 0);
            this.isSubmitting = true;
            clearTimeout(this._addedResetTimeout);
            this.submitButton.dataset.added = 'true';
            this.setStatus('');

            const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
              },
              body: JSON.stringify({
                items,
                sections: getCartSectionIds(),
                sections_url: window.location.pathname + window.location.search
              })
            });

            const result = await response.json();
            if (!response.ok) {
              throw new Error(result.description || result.message || cfg.errorAddFailed);
            }

            // The whole cart, not just the added lines: the upsell popup and product form read it.
            const cartResponse = await fetch('/cart.js');
            const cart = await cartResponse.json();

            this.dispatchEvent(new CartAddEvent(cart, this.id || elementName, {
              source: 'refillable-builder',
              productId: this.dataset.productId,
              itemCount: Number(cart.item_count || addedItemCount),
              sections: result.sections || {}
            }));

            this.openCartDrawerIfEnabled();
            this.setStatus(cfg.statusAdded);

            // Same hold as Horizon's AddToCartComponent: let the checkmark finish, then 800ms.
            await Promise.allSettled(this.submitButton.getAnimations({ subtree: true }).map((animation) => animation.finished));
            this._addedResetTimeout = setTimeout(() => this.submitButton.removeAttribute('data-added'), 800);
          } catch (error) {
            this.submitButton.removeAttribute('data-added');
            const message = error instanceof Error && error.message ? error.message : cfg.errorAddFailed;
            this.setStatus(message, 'error');
          } finally {
            this.isSubmitting = false;
          }
        }

        // --- Shareable URL state -------------------------------------------------
        // Selections round-trip through the query string, so a build can be linked to
        // from a campaign or reopened by the customer. Scents key off the scent entry's
        // handle (blue), which is never translated, not the option value (Blue -> Blauw)
        // or the `scent_code` text metafield, which auto-translation also localises.

        getScentUrlMaps() {
          if (this._scentUrlMaps) return this._scentUrlMaps;
          const codeToSlot = new Map();
          const slotToCode = new Map();
          (this.data?.refillProduct?.variants || []).forEach((variant) => {
            const slot = this.getScentSlotKey(variant);
            if (!slot) return;
            const code = String(variant.scentCode || '').trim().toLowerCase();
            // A code carrying the delimiters would not survive the round trip, so ignore it
            // and let the slot token stand in. The metafield definition also rejects these.
            if (!code || /[,:]/.test(code) || codeToSlot.has(code)) return;
            codeToSlot.set(code, slot);
            if (!slotToCode.has(slot)) slotToCode.set(slot, code);
          });
          this._scentUrlMaps = { codeToSlot, slotToCode };
          return this._scentUrlMaps;
        }

        // A scent with no code still round-trips, just on its slot token.
        /**
         * @param {string} slot
         */
        scentSlotToUrlToken(slot) {
          return this.getScentUrlMaps().slotToCode.get(slot) || slot;
        }

        /**
         * @param {string} token
         */
        urlTokenToScentSlot(token) {
          const raw = String(token || '').trim().toLowerCase();
          if (!raw) return null;
          const codeToSlot = this.getScentUrlMaps().codeToSlot;
          if (codeToSlot.has(raw)) return codeToSlot.get(raw);
          const isKnownSlot = (this.data?.refillProduct?.variants || [])
            .some((variant) => this.getScentSlotKey(variant) === raw);
          return isKnownSlot ? raw : null;
        }

        // A link outlives the stock it was built from. Restoring a quantity for a sold-out
        // scent would suppress its sold-out label (that label is skipped whenever a quantity
        // is set) and leave a dead + button with nothing explaining why.
        //
        // Mode and case are passed in rather than read off state: this runs while parsing,
        // before the link's own values have been applied, so state still holds the defaults.
        /**
         * @param {string} slot
         * @param {string} mode
         * @param {string | null} caseKey
         */
        isScentSlotPurchasable(slot, mode, caseKey) {
          const variant = (this.data?.refillProduct?.variants || [])
            .find((candidate) => this.getScentSlotKey(candidate) === slot);
          const refillAvailable = Boolean(variant && variant.available && variant.id);
          if (mode !== 'starter' || !this.hasStarterPack) return refillAvailable;
          // In starter mode a scent is reachable either as the pack itself or as an extra refill.
          return refillAvailable || Boolean(this.getStarterPackVariantForSelection(caseKey, slot));
        }

        getValidFrequencyValues() {
          return Array.from(this.frequencySelect?.options || [], (option) => option.value).filter(Boolean);
        }

        /**
         * @param {string | null} key
         * @param {string} mode
         */
        isValidCaseKey(key, mode) {
          if (mode !== 'starter') return false;
          if (this.hasStarterPack) {
            return this.getStarterPackCaseColours().some((colour) => colour.key === key);
          }
          return this.getCaseVariants().some((variant) => String(variant.id) === String(key));
        }

        // Every field is validated against live product data and dropped when it does not
        // hold up, so a stale or hand-mangled link degrades to the default builder.
        /**
         * @typedef {Partial<Pick<BuilderState, 'mode' | 'plan' | 'frequency' | 'caseVariantId' | 'scentQuantities'>>} UrlState
         * @returns {UrlState | null}
         */
        readUrlState() {
          let params;
          try {
            const token = new URLSearchParams(window.location.search).get(URL_STATE_PARAM);
            if (!token) return null;
            const payload = decodeBuilderToken(token);
            if (payload === null) return null;
            params = new URLSearchParams(payload);
          } catch (error) {
            return null;
          }
          if (!URL_STATE_KEYS.some((key) => params.has(key))) return null;

          /** @type {UrlState} */
          const next = {};

          const mode = params.get('mode');
          if (mode === 'starter' || mode === 'refill') next.mode = mode;
          const resolvedMode = next.mode || this.state.mode;

          const plan = params.get('plan');
          if (plan === 'subscription' || plan === 'onetime') next.plan = plan;

          const frequency = params.get('freq');
          if (frequency && this.getValidFrequencyValues().includes(String(frequency))) {
            next.frequency = String(frequency);
          }

          const caseKey = params.get('case');
          if (caseKey && this.isValidCaseKey(caseKey, resolvedMode)) next.caseVariantId = caseKey;

          if (params.has('scents')) {
            // Falls back to the default colour the constructor picked when the link omits one.
            const resolvedCaseKey = next.caseVariantId || this.state.caseVariantId;
            /** @type {Record<string, number>} */
            const quantities = {};
            String(params.get('scents')).split(',').forEach((pair) => {
              const separator = pair.indexOf(':');
              const token = separator === -1 ? pair : pair.slice(0, separator);
              const slot = this.urlTokenToScentSlot(token);
              if (!slot || quantities[slot]) return;
              if (!this.isScentSlotPurchasable(slot, resolvedMode, resolvedCaseKey)) return;
              const rawQuantity = separator === -1 ? '1' : pair.slice(separator + 1);
              const quantity = Math.floor(Number(rawQuantity));
              if (!Number.isFinite(quantity) || quantity < 1) return;
              quantities[slot] = Math.min(quantity, MAX_PER_SCENT);
            });
            // A starter-mode link naming only ineligible scents would restore a build that
            // throws on add to cart, so it degrades to the default builder instead.
            if (resolvedMode === 'starter' && this.hasStarterPack) {
              const hasEligible = Object.keys(quantities)
                .some((slot) => Boolean(this.getStarterPackVariantForSelection(resolvedCaseKey, slot)));
              if (!hasEligible) return null;
            }
            if (Object.keys(quantities).length) next.scentQuantities = quantities;
          }

          return Object.keys(next).length ? next : null;
        }

        // Runs before the first render pass, and writes straight into state rather than
        // replaying setMode/setPlan - those reset sibling fields as a side effect.
        applyUrlState() {
          const next = this.readUrlState();
          if (!next) return;

          if (next.mode) {
            this.state.mode = next.mode;
            this.state.hasSelectedMode = true;
            if (next.mode === 'refill') this.state.caseVariantId = null;
          }
          if (next.caseVariantId) {
            this.state.caseVariantId = next.caseVariantId;
            this.state.hasInteractedWithCaseVariant = true;
          }
          if (next.scentQuantities) {
            this.state.scentQuantities = next.scentQuantities;
            const [firstKey] = Object.keys(next.scentQuantities);
            if (firstKey) this.state.lastInteractedScentKey = firstKey;
          }
          if (next.plan) this.state.plan = next.plan;
          if (next.frequency) this.state.frequency = next.frequency;
          // A one-time build has no frequency, whatever the link claimed.
          if (this.state.plan !== 'subscription') this.state.frequency = '3';

          this.autoSelectSubscriptionScent();
          this.autoSelectStarterPackScent();

          // The starter pack is built from one of the selected scents, and auto-select just
          // takes the first. A link can lead with a scent the pack does not offer, which only
          // surfaces as a failed add-to-cart, so re-point it at one that resolves - or drop the
          // selection entirely if none does.
          if (this.hasStarterPack && this.state.mode === 'starter' && this.state.starterPackScentKey) {
            const caseKey = this.state.caseVariantId;
            if (!this.getStarterPackVariantForSelection(caseKey, this.state.starterPackScentKey)) {
              const packable = Object.keys(this.state.scentQuantities)
                .find((slot) => this.getStarterPackVariantForSelection(caseKey, slot));
              if (packable) {
                this.state.starterPackScentKey = packable;
              } else {
                this.state.scentQuantities = {};
                this.state.starterPackScentKey = null;
                this.state.subscriptionScentKey = null;
                this.state.lastInteractedScentKey = this.getDefaultScentKey();
              }
            }
          }
        }

        // Builds the token for the current selection. Deliberately NOT called on state change:
        // writing to the address bar fires a history event that GA4's enhanced measurement
        // counts as a page view, which inflated PDP views once per click. Kept so a link can be
        // produced from the page, and for a share control if one is ever added.
        getShareToken() {
          const parts = [`mode=${this.state.mode}`, `plan=${this.state.plan}`];
          if (this.state.mode === 'starter' && this.state.caseVariantId) {
            parts.push(`case=${this.state.caseVariantId}`);
          }
          const scents = Object.entries(this.state.scentQuantities)
            .filter(([, quantity]) => Number(quantity) > 0)
            .map(([slot, quantity]) => `${this.scentSlotToUrlToken(slot)}:${quantity}`);
          if (scents.length) parts.push(`scents=${scents.join(',')}`);
          if (this.state.plan === 'subscription') parts.push(`freq=${this.state.frequency}`);
          return encodeBuilderToken(parts.join('&'));
        }

        getShareUrl() {
          const url = new URL(window.location.href);
          url.search = '';
          url.searchParams.set(URL_STATE_PARAM, this.getShareToken());
          return url.toString();
        }

        emitStateChange() {
          const detail = {
            mode: this.state.mode,
            plan: this.state.plan,
            frequency: this.state.frequency,
            subscriptionScentKey: this.state.subscriptionScentKey,
            stepTwoVariantId: this.getSelectedBaseVariantId(),
            scentQuantities: { ...this.state.scentQuantities }
          };

          this.dispatchEvent(new CustomEvent('refillable-builder:statechange', {
            bubbles: true,
            detail
          }));
        }
      }

      if (!customElements.get(elementName)) customElements.define(elementName, RefillableParfumBuilder);
